import MojitoCore from 'mojito/core';
import { createSlice } from '@reduxjs/toolkit';
import ServicesTypes from 'services/common/types.js';
import ServicesUtils from 'services/common/utils.js';
import { omit, isEmpty, keyBy } from 'mojito/utils';
import EventDataDescriptors from './descriptors.js';
import channelFactory from 'services/common/content/content-channel-factory.js';
import eventProvider from 'services/sports-content/content-provider/event-provider';
import marketProvider from 'services/sports-content/content-provider/market-provider';
import eventTipsProvider from 'services/sports-content/content-provider/event-tips-provider';

const reduxInstance = MojitoCore.Services.redux;
const { UNKNOWN, AVAILABLE, UNAVAILABLE } = ServicesTypes.CONTENT_STATE;
const { EVENT, EVENT_CHUNK, EVENT_MARKET, EVENT_MARKET_CHUNK, EVENT_TIPS } =
    EventDataDescriptors.DATA_TYPES;
const { actionsRegistry } = MojitoCore.Services.Content;

export const getEventChannel = () => channelFactory.getChannel(eventProvider, EVENT);
export const getMarketChannel = () => channelFactory.getChannel(marketProvider, EVENT_MARKET);
export const getEventTipsChannel = () => channelFactory.getChannel(eventTipsProvider, EVENT_TIPS);

/**
 * Defines the state of the events store. Contains events and markets.
 *
 * @typedef EventsState
 *
 * @property {object} events - Map of events. Key is event id and value is event content object.
 * @property {object} markets - Map of markets. Key is market id and value is market content object.
 * @property {object} tips - Map of tips. Key is event id and value is the tips info object that contains tips list and some technical info about the storable.
 * @property {Object<string, Mojito.Services.Common.types.CONTENT_STATE>} eventsState - Map of events lifetime state. Key is event id and value is of type {@link Mojito.Services.SportsContent.Common.types.CONTENT_STATE|CONTENT_STATE}.
 * @property {Object<string, Mojito.Services.Common.types.CONTENT_STATE>} marketsState - Map of markets lifetime state. Key is market id and value is of type {@link Mojito.Services.SportsContent.Common.types.CONTENT_STATE|CONTENT_STATE}.
 * @property {Object<string, Mojito.Services.Common.types.CONTENT_STATE>} tipsState - Map of tips lifetime state. Key is event id and value is of type {@link Mojito.Services.SportsContent.Common.types.CONTENT_STATE|CONTENT_STATE}.
 *
 * @memberof Mojito.Services.SportsContent.Events
 */

/**
 * The name of the events store. Will be used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.SportsContent.Events
 */
export const STORE_KEY = 'eventsStore';

export const INITIAL_STATE = {
    events: {},
    markets: {},
    tips: {},
    eventsState: {},
    marketsState: {},
    tipsState: {},
};

const reducers = {
    updateEvent(state, { payload }) {
        const { eventId, event } = payload;
        if (event) {
            state.events[eventId] = event;
            state.eventsState[eventId] = AVAILABLE;
        } else if (eventId) {
            delete state.events[eventId];
            state.eventsState[eventId] = UNAVAILABLE;
        }
    },
    updateEvents(state, { payload: eventInfos }) {
        eventInfos.forEach(eventInfo => {
            const payload = { eventId: eventInfo.id, event: eventInfo.data };
            reducers.updateEvent(state, { payload });
        });
    },
    updateMarket(state, { payload }) {
        const { marketId, market } = payload;
        if (market) {
            const marketCopy = { ...market };
            marketCopy.selectionMap = keyBy(market.selections || [], 'id');
            state.markets[marketId] = marketCopy;
            state.marketsState[marketId] = AVAILABLE;
        } else {
            delete state.markets[marketId];
            state.marketsState[marketId] = UNAVAILABLE;
        }
    },
    updateMarkets(state, { payload: marketInfos }) {
        marketInfos.forEach(marketInfo => {
            const payload = {
                marketId: marketInfo.id,
                market: marketInfo.data,
            };
            reducers.updateMarket(state, { payload });
        });
    },
    updateEventTips(state, { payload }) {
        const { eventId, tips } = payload;
        if (tips) {
            state.tips[eventId] = tips;
            state.tipsState[eventId] = AVAILABLE;
        } else if (eventId) {
            delete state.tips[eventId];
            state.tipsState[eventId] = UNAVAILABLE;
        }
    },
    removeEvents(state, { payload }) {
        const { eventIds = [] } = payload;
        state.events = omit(state.events, eventIds);
        eventIds.forEach(eventId => (state.eventsState[eventId] = UNKNOWN));
    },
    removeMarkets(state, { payload }) {
        const { marketIds = [] } = payload;
        state.markets = omit(state.markets, marketIds);
        marketIds.forEach(marketId => (state.marketsState[marketId] = UNKNOWN));
    },
    removeEventTips(state, { payload }) {
        const { eventIds = [] } = payload;
        state.tips = omit(state.tips, eventIds);
        eventIds.forEach(eventId => (state.tipsState[eventId] = UNKNOWN));
    },
    pendingEvents: ServicesUtils.createPendingHandler('eventsState'),
    pendingMarkets: ServicesUtils.createPendingHandler('marketsState'),
    pendingTips: ServicesUtils.createPendingHandler('tipsState'),
    reset() {
        return { ...INITIAL_STATE };
    },
};

export const { reducer, actions } = createSlice({
    name: 'events',
    initialState: INITIAL_STATE,
    reducers,
});

/**
 * Events store actions.
 *
 * @class EventsActions
 * @name actions
 * @memberof Mojito.Services.SportsContent.Events
 */

/**
 * Update event.
 *
 * @function updateEvent
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} payload - Payload for event update.
 * @param {string} payload.eventId - Event id.
 * @param {object|undefined} payload.event - Event object.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Update events.
 *
 * @function updateEvents
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {Array<{id: string, data: object}>} payload - Payload for events list update.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Update market.
 *
 * @function updateMarket
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{eventId: string, marketId: string, market: object|undefined}} payload - Payload for market update.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Update markets.
 *
 * @function updateMarkets
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {Array<{id: string, data: object, eventId: string}>} payload - Payload for market list update.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Update event tips.
 *
 * @function updateEventTips
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {object} payload - Payload for event update.
 * @param {string} payload.eventId - Event id.
 * @param {object} payload.tips - Tips object.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Remove events.
 *
 * @function removeEvents
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{eventIds: Array<string>}} payload - Payload for remove events.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Remove markets.
 *
 * @function removeMarkets
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{marketIds: Array<string>}} payload - Payload for remove markets.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Remove event tips.
 *
 * @function removeEventTips
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{eventIds: Array<string>}} payload - Payload for removing event tips.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Pending events.
 *
 * @function pendingEvents
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{eventIds: Array<string>}} payload - Payload contains the ids of pending events.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Pending markets.
 *
 * @function pendingMarkets
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{marketIds: Array<string>}} payload - Payload contains the ids of pending markets.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Pending event tips.
 *
 * @function pendingTips
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @param {{eventIds: Array<string>}} payload - Payload contains the ids of pending tips.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Reset events state.
 *
 * @function reset
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.SportsContent.Events.actions
 */

/**
 * Subscribes to events.
 *
 * @function subscribeEvents
 *
 * @param {{clientId: string, eventIds: Array<string>}} payload - Subscription payload. Contains the list of event ids to subscribe to and the id of subscriber.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe to events thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.subscribeEvents = payload => {
    return dispatch => {
        const { clientId, eventIds } = payload;
        const pendingEventIds = getEventChannel().subscribe(
            eventIds,
            clientId,
            (eventId, event) => {
                dispatch(actions.updateEvent({ eventId, event }));
            }
        );
        !isEmpty(pendingEventIds) && dispatch(actions.pendingEvents(pendingEventIds));
    };
};

/**
 * Triggers events chunk subscription for multiple clients.
 *
 * @function subscribeEventsChunk
 *
 * @param {object} payload - Subscription payload. Contains the list of event requests for each client subscriber.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe to events thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.subscribeEventsChunk = payload => {
    return dispatch => {
        const { requests } = payload;

        const onInit = chunk => dispatch(actions.updateEvents(chunk));
        const onUpdate = (eventId, event) => dispatch(actions.updateEvent({ eventId, event }));

        const pendingEventIds = getEventChannel().subscribeMultiple(requests, onInit, onUpdate);
        queueMicrotask(() => {
            !isEmpty(pendingEventIds) && dispatch(actions.pendingEvents(pendingEventIds));
        });
    };
};

/**
 * Subscribes to markets.
 *
 * @function subscribeMarkets
 *
 * @param {{clientId: string, eventId: string, marketIds: Array<string>}} payload - Subscription payload. Contains the list of market ids to subscribe to, eventId to which markets belongs and the id of subscriber.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe to markets thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.subscribeMarkets = payload => {
    return dispatch => {
        const { clientId, marketIds } = payload;
        const onData = (marketId, market) => {
            dispatch(actions.updateMarket({ marketId, market }));
        };
        const pendingMarketIds = getMarketChannel().subscribe(marketIds, clientId, onData);
        !isEmpty(pendingMarketIds) && dispatch(actions.pendingMarkets(pendingMarketIds));
    };
};

/**
 * Triggers market chunk subscription for multiple clients.
 *
 * @function subscribeMarketsChunk
 *
 * @param {object} payload - Subscription payload. Contains the list of market requests for each client subscriber.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe to events thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.subscribeMarketsChunk = payload => {
    return dispatch => {
        const { requests } = payload;
        const onInit = marketsChunk => {
            dispatch(actions.updateMarkets(marketsChunk));
        };
        const onUpdate = (marketId, market) => {
            dispatch(actions.updateMarket({ marketId, market }));
        };
        const pendingMarketIds = getMarketChannel().subscribeMultiple(requests, onInit, onUpdate);
        queueMicrotask(() => {
            !isEmpty(pendingMarketIds) && dispatch(actions.pendingMarkets(pendingMarketIds));
        });
    };
};

/**
 * Unsubscribe a client from specific events.
 *
 * @function unsubscribeEvents
 *
 * @param {Array<string>} eventIds - Event ids to unsubscribe from.
 * @param {string} clientId - The id of the subscriber.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe from events thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.unsubscribeEvents = (eventIds, clientId) => {
    return dispatch => {
        const disposedEventIds = getEventChannel().unsubscribe(eventIds, clientId);
        !isEmpty(disposedEventIds) &&
            dispatch(actions.removeEvents({ eventIds: disposedEventIds }));
    };
};

/**
 * Unsubscribe client from all events.
 *
 * @function unsubscribeAllEvents
 *
 * @param {string} clientId - The id of subscriber which aims to be unsubscribed.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe from events thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.unsubscribeAllEvents = clientId => {
    return dispatch => {
        getEventChannel().unsubscribeAll(clientId, eventIds => {
            dispatch(actions.removeEvents({ eventIds }));
        });
    };
};

/**
 * Unsubscribe client from all markets.
 *
 * @function unsubscribeAllMarkets
 *
 * @param {string} clientId - The id of subscriber which aims to be unsubscribed.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe from markets thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.unsubscribeAllMarkets = clientId => {
    return dispatch => {
        getMarketChannel().unsubscribeAll(clientId, marketIds => {
            dispatch(actions.removeMarkets({ marketIds }));
        });
    };
};

/**
 * Subscribes to event tips.
 *
 * @function subscribeEventTips
 *
 * @param {{clientId: string, eventIds: Array<string>}} payload - Subscription payload. Contains the list of event ids to subscribe to and the id of subscriber.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Subscribe to event tips thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.subscribeEventTips = payload => {
    return dispatch => {
        const { clientId, eventIds } = payload;
        const pendingEventIds = getEventTipsChannel().subscribe(
            eventIds,
            clientId,
            (eventId, tips) => {
                dispatch(actions.updateEventTips({ eventId, tips }));
            }
        );
        !isEmpty(pendingEventIds) && dispatch(actions.pendingTips(pendingEventIds));
    };
};

/**
 * Unsubscribe client from all event tips.
 *
 * @function unsubscribeEventTips
 *
 * @param {string} clientId - The id of subscriber which aims to be unsubscribed.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Unsubscribe from event tips thunk.
 * @memberof Mojito.Services.SportsContent.Events.actions
 */
actions.unsubscribeEventTips = clientId => {
    return dispatch => {
        getEventTipsChannel().unsubscribeAll(clientId, eventIds => {
            dispatch(actions.removeEventTips({ eventIds }));
        });
    };
};

// Register thunks in common registry to use them by request data helper.
actionsRegistry.addSubscription(EVENT, actions.subscribeEvents, actions.unsubscribeAllEvents);
actionsRegistry.addSubscription(
    EVENT_CHUNK,
    actions.subscribeEventsChunk,
    actions.unsubscribeAllEvents
);
actionsRegistry.addSubscription(
    EVENT_MARKET,
    actions.subscribeMarkets,
    actions.unsubscribeAllMarkets
);
actionsRegistry.addSubscription(
    EVENT_MARKET_CHUNK,
    actions.subscribeMarketsChunk,
    actions.unsubscribeAllMarkets
);
actionsRegistry.addSubscription(
    EVENT_TIPS,
    actions.subscribeEventTips,
    actions.unsubscribeEventTips
);

reduxInstance.injectReducer(STORE_KEY, reducer);
